import React, {memo, useCallback, useMemo, useState} from 'react';
import {
    Animated,
    Modal,
    StyleSheet,
    Text,
    TouchableHighlight,
    TouchableOpacity,
    View
} from 'react-native';
import {Ionicons, MaterialIcons} from '@expo/vector-icons';
import Feather from '@expo/vector-icons/Feather';
import {usePosModeSwitch} from '../helpers/usePosModeSwitch';
import {haptics} from '../helpers/haptics';
import {useTranslation} from 'react-i18next';
import '../../i18n';

/**
 * Order-type switcher button (quick receipt / tables / delivery).
 *
 * @param {object} props
 * @param {object} props.navigation Navigation object of the POS host screen.
 * @param {string} props.iconName Ionicons glyph for the button.
 * @param {object} [props.style] Extra style for the round button.
 * @param {boolean} [props.inline=false] Render in normal flow (inside the phone
 *   PosTabBar centre slot) instead of absolutely positioned over the bar.
 * @param {number} [props.size=60] Button diameter in dp.
 * @param {boolean} [props.showHub=false] Add a "POS Hub" entry (phone UI only; the Hub is a phone screen).
 */
const CircularButton = ({ navigation, iconName, style, inline = false, size = 60, showHub = false }) => {
    const { t } = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);
    const switchMode = usePosModeSwitch(navigation);

    // Closes the sheet, then applies the shared cart / table reset rules (usePosModeSwitch).
    const handleSelect = useCallback((value) => {
        setModalVisible(false);
        switchMode(value);
    }, [switchMode]);

    const scaleValue = React.useRef(new Animated.Value(1)).current;

    // Memoize animation functions to prevent recreation on each render
    const handleMouseEnter = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1.4,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    const handleMouseLeave = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    const handlePressIn = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1.4,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    const handlePressOut = useCallback(() => {
        Animated.timing(scaleValue, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [scaleValue]);

    // Open the order-type sheet on a real press. Previously this was wired to
    // onPressOut only, which on React-Native-Web maps to pointer/mouse-up and is
    // NOT triggered by a plain click / synthetic event, so the FAB appeared dead
    // (SVC-014). onPress fires reliably for tap, mouse click and keyboard.
    const handleOpenMenu = useCallback(() => {
        haptics.tap();
        setModalVisible(true);
    }, []);

    // Memoize the button style to prevent recreation on each render
    const buttonStyle = useMemo(() => [
        styles.circularButton,
        { width: size, height: size, borderRadius: size / 2 },
        // `margin: auto` only makes sense inside the absolutely positioned wrapper.
        inline && { margin: 0 },
        style,
        { transform: [{ scale: scaleValue }]},
    ], [style, scaleValue, inline, size]);

    return (
        <View style={inline ? styles.inlineContainer : styles.menuContainer}>
            <TouchableOpacity
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel={t('order_type_menu_label', { defaultValue: 'Order type menu' })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onPressIn={handlePressIn}
                onPressOut={handlePressOut}
                onPress={handleOpenMenu}
                style={buttonStyle}
            >
                <Ionicons name={iconName} size={Math.round(size * 0.5)} style={styles.icon} />
            </TouchableOpacity>

            <Modal
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <TouchableOpacity
                    style={styles.modalOverlay}
                    activeOpacity={1}
                    onPressOut={() => setModalVisible(false)}
                >
                    {/* Memoize the modal content to prevent unnecessary re-renders */}
                    {useMemo(() => (
                        <View style={styles.modalContent}>
                            {showHub && (
                                <TouchableHighlight
                                    activeOpacity={0.9}
                                    underlayColor={'#ffefc1'}
                                    onPress={() => handleSelect('hub')}
                                    style={styles.modalItem}
                                >
                                    <View style={styles.buttonRow}>
                                        <Ionicons name="grid-outline" size={36} color="#F97316" />
                                        <Text style={[styles.modalText, {color: "#F97316"}]}>{t('pos_hub_title', 'POS Hub')}</Text>
                                    </View>
                                </TouchableHighlight>
                            )}
                            <TouchableHighlight 
                                activeOpacity={0.9} 
                                underlayColor={"#ffefc1"} 
                                onPress={() => handleSelect('takeAway')} 
                                style={styles.modalItem}
                            >
                                <View style={styles.buttonRow}>
                                    <Ionicons name="bag-outline" size={36} color="#000" />
                                    <Text style={[styles.modalText,{color: "#000"}]}>{t("quick_receipt")}</Text>
                                </View>
                            </TouchableHighlight>
                            <TouchableHighlight 
                                activeOpacity={0.9} 
                                underlayColor={'#ffefc1'} 
                                onPress={() => handleSelect('dineIn')} 
                                style={styles.modalItem}
                            >
                                <View style={styles.buttonRow}>
                                    <MaterialIcons name="table-bar" size={36} color="#F1B44C" />
                                    <Text style={[styles.modalText,{color: "#F1B44C"}]}>{t("table_map")}</Text>
                                </View>
                            </TouchableHighlight>
                            <TouchableHighlight 
                                activeOpacity={0.9} 
                                underlayColor={'#ffefc1'} 
                                onPress={() => handleSelect('delivery')} 
                                style={styles.modalItem}
                            >
                                <View style={styles.buttonRow}>
                                    <Feather name="truck" size={36} color="#50A5F1" />
                                    <Text style={[styles.modalText,{color: "#50A5F1"}]}>{t("home_delivery")}</Text>
                                </View>
                            </TouchableHighlight>
                        </View>
                    ), [t, handleSelect, showHub])}
                </TouchableOpacity>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    buttonRow: {

        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 20,
        width: '100%',
    },
    circularButton: {
        margin: "auto",
        width: 60,
        height: 60,
        borderRadius: 30,
        backgroundColor: '#F1B34B',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 999, // Ensure the button is above other elements
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 3,
        },
        shadowOpacity: 0.27,
        shadowRadius: 4.65,

        elevation: 6,

    },
    inlineContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999,
    },
    menuContainer: {
        position: 'absolute',
        bottom: 40,
        left: '50%',
        transform: [{ translateX: -25 }],
        zIndex: 999, // Ensure the menu is above other elements
    },
    modalOverlay: {
        flex: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    modalContent: {
        width: '92%',
        maxWidth: 400,
        backgroundColor: '#fff',
        borderRadius: 10,
        padding: 15,
        marginTop: 15,

        alignItems: 'center',
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 7,
        },
        shadowOpacity: 0.30,
        shadowRadius: 7,
        elevation: 3,
        marginBottom: 70, // Position the modal above the circular button
    },
    modalItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 17,
        width: '100%',
        minHeight: 72, // big, thumb-friendly rows
        borderBottomColor: '#EEE',
        borderBottomWidth: 0.7,

        paddingTop: 12,
        paddingBottom: 12,

        textAlign: "center",
        justifyContent: "space-between",
    },
    modalText: {
        marginLeft: 20,
        fontSize: 22,
        fontWeight: '900',
        textAlign: 'center',
    },
    icon: {
        color: '#fff',
    },
});

export default memo(CircularButton);
